import React from 'react';
import Icon from '../../../components/AppIcon';

const TechnicalTestimonial = ({ testimonial, layout = 'card' }) => {
  if (layout === 'inline') {
    return (
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-6 border-l-4 border-brand-primary">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-brand-primary rounded-full flex items-center justify-center">
              <Icon name="Quote" size={20} className="text-white" />
            </div>
          </div>
          <div className="flex-1">
            <blockquote className="text-text-primary font-medium leading-relaxed mb-4">
              "{testimonial?.quote}"
            </blockquote>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold text-text-primary">{testimonial?.author}</div>
                <div className="text-sm text-text-secondary">{testimonial?.title}</div>
                <div className="text-sm text-brand-primary font-medium">{testimonial?.company}</div>
              </div>
              <div className="text-right">
                <div className="flex items-center space-x-1 mb-1">
                  {[...Array(5)]?.map((_, i) => (
                    <Icon 
                      key={i} 
                      name="Star" 
                      size={14} 
                      className={i < testimonial?.rating ? "text-yellow-400 fill-current" : "text-gray-300"} 
                    />
                  ))}
                </div>
                <div className="text-xs text-text-secondary">{testimonial?.implementationTime}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-executive p-8 border border-gray-200 hover:shadow-executive-lg transition-all duration-300">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <div className="w-16 h-16 bg-gradient-to-br from-brand-primary to-brand-secondary rounded-xl flex items-center justify-center">
            <Icon name="User" size={24} className="text-white" />
          </div>
          <div>
            <h4 className="font-bold text-text-primary text-lg">{testimonial?.author}</h4>
            <p className="text-text-secondary">{testimonial?.title}</p>
            <p className="text-brand-primary font-medium text-sm">{testimonial?.company}</p>
          </div>
        </div>
        <div className="text-right">
          <div className="flex items-center space-x-1 mb-2">
            {[...Array(5)]?.map((_, i) => (
              <Icon 
                key={i} 
                name="Star" 
                size={16} 
                className={i < testimonial?.rating ? "text-yellow-400 fill-current" : "text-gray-300"} 
              />
            ))}
          </div>
          <div className="text-xs text-text-secondary">{testimonial?.implementationTime}</div>
        </div>
      </div>
      {/* Quote */}
      <blockquote className="text-text-primary leading-relaxed mb-6 text-lg">
        <Icon name="Quote" size={24} className="text-brand-primary/30 mb-2" />
        "{testimonial?.quote}"
      </blockquote>
      {/* Key Metrics */}
      {testimonial?.metrics && (
        <div className="mb-6">
          <h5 className="font-semibold text-text-primary mb-3">Results Achieved</h5>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {testimonial?.metrics?.map((metric, index) => (
              <div key={index} className="text-center p-3 bg-gray-50 rounded-lg">
                <div className="text-xl font-bold text-brand-primary">{metric?.value}</div>
                <div className="text-sm text-text-secondary">{metric?.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Technical Details */}
      {testimonial?.technicalDetails && (
        <div className="mb-6">
          <h5 className="font-semibold text-text-primary mb-3">Technical Implementation</h5>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h6 className="font-medium text-text-primary mb-2">Technologies Used</h6>
                <div className="flex flex-wrap gap-2">
                  {testimonial?.technicalDetails?.technologies?.map((tech, index) => (
                    <span 
                      key={index}
                      className="px-2 py-1 bg-white border border-gray-200 rounded text-xs font-medium text-text-primary"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <h6 className="font-medium text-text-primary mb-2">Implementation Scope</h6>
                <ul className="space-y-1">
                  {testimonial?.technicalDetails?.scope?.map((item, index) => (
                    <li key={index} className="text-sm text-text-secondary flex items-center">
                      <Icon name="CheckCircle" size={12} className="text-green-500 mr-2 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Challenges Overcome */}
      {testimonial?.challenges && (
        <div className="mb-6">
          <h5 className="font-semibold text-text-primary mb-3">Key Challenges Overcome</h5>
          <div className="space-y-3">
            {testimonial?.challenges?.map((challenge, index) => (
              <div key={index} className="flex items-start space-x-3">
                <Icon name="AlertTriangle" size={16} className="text-orange-500 mt-0.5 flex-shrink-0" />
                <div>
                  <h6 className="font-medium text-text-primary text-sm">{challenge?.challenge}</h6>
                  <p className="text-sm text-text-secondary mt-1">{challenge?.solution}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Footer */}
      <div className="flex items-center justify-between pt-6 border-t border-gray-100">
        <div className="flex items-center space-x-2">
          <Icon name="Calendar" size={16} className="text-text-secondary" />
          <span className="text-sm text-text-secondary">Implemented: {testimonial?.implementationDate}</span>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="Building" size={16} className="text-text-secondary" />
          <span className="text-sm text-text-secondary">{testimonial?.companySize}</span>
        </div>
      </div>
    </div>
  );
};

export default TechnicalTestimonial;